import { useMemo } from 'react'
import { MoneySummary } from '@/components/money/MoneySummary'
import { parseIndexExactIdSearch } from '@/components/tables/clientIndexTable'
import {
  IndexDataTable,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import type { PathHelpers } from '@/hooks/usePathHelper'
import type {
  CustomerBalanceReport,
  CustomerBalanceReportCustomer,
} from '@/lib/services/reports/reports.types'
import { formatCurrency, parseCurrency } from '@/lib/utils'
import { CalendarClock, DollarSign, ReceiptText, Users } from 'lucide-react'
import { getCustomerBalanceReportColumns } from './-table-columns'

const agingBucketRows = [
  { key: 'current', label: 'Current', description: 'Not past due' },
  { key: 'days_1_30', label: '1-30 days', description: 'Past due' },
  { key: 'days_31_60', label: '31-60 days', description: 'Past due' },
  { key: 'days_61_90', label: '61-90 days', description: 'Past due' },
  { key: 'days_91_plus', label: '91+ days', description: 'Past due' },
] as const

type FormatPhone = (phone: string) => string

type CustomerBalanceReportResultsProps = {
  report: CustomerBalanceReport | undefined
  /** The `$orgId` route param, parsed to a number. */
  orgId: number
  path: PathHelpers
  formatPhone: FormatPhone
  searchTerm?: string
  onSearchChange: (searchTerm: string) => void
  isFetching?: boolean
}

export function CustomerBalanceReportResults({
  report,
  orgId,
  path,
  formatPhone,
  searchTerm = '',
  onSearchChange,
  isFetching = false,
}: CustomerBalanceReportResultsProps) {
  const summary = report?.summary
  const aging = summary?.aging
  const openBalanceTotal = parseCurrency(summary?.open_balance_total)
  const customers = report?.customers ?? []
  const filteredCustomers = filterCustomerBalanceCustomers(customers, searchTerm, formatPhone)
  // Keep cell component types stable through query refreshes and link preloads.
  const columns = useMemo(() => getCustomerBalanceReportColumns({ formatPhone }), [formatPhone])
  const hasCustomers = customers.length > 0 || searchTerm.trim() !== ''
  const customerCount = filteredCustomers.length

  return (
    <div className="@container/balances space-y-5">
      <MoneySummary
        title="Open balances"
        description="Open customer balances as of the selected date."
        metrics={[
          {
            title: 'Open Balance',
            value: formatCurrency(summary?.open_balance_total ?? 0),
            description: 'Total unpaid customer balance',
            icon: DollarSign,
            tone: 'warning',
            accent: 'warning',
          },
          {
            title: 'Past Due',
            value: formatCurrency(summary?.overdue_balance_total ?? 0),
            description: 'Total balance after due date',
            icon: CalendarClock,
            tone: 'destructive',
            valueTone: 'destructive',
          },
          {
            title: 'Customers',
            value: String(summary?.open_customer_count ?? 0),
            description: 'Customers with open balances',
            icon: Users,
            tone: 'info',
          },
          {
            title: 'Open Invoices',
            value: String(summary?.open_invoice_count ?? 0),
            description: 'Orders with a balance',
            icon: ReceiptText,
            tone: 'brand',
          },
        ]}
      />

      <Card>
        <CardHeader>
          <div className="space-y-1.5">
            <CardTitle>How long balances are overdue</CardTitle>
            <CardDescription>
              See what’s due and how long each balance has been open.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-5 @xl/balances:grid-cols-2 @4xl/balances:grid-cols-5">
            {agingBucketRows.map((bucket, index) => (
              <div key={bucket.key} className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Typography variant="labelStrong">{bucket.label}</Typography>
                  <Typography variant="caption">{bucket.description}</Typography>
                </div>
                <Typography
                  variant="h4"
                  tone={index === 0 ? 'success' : index < 3 ? 'warning' : 'destructive'}
                  className="tabular-nums"
                >
                  {formatCurrency(aging?.[bucket.key] ?? 0)}
                </Typography>
                <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${openBalanceTotal > 0 ? (parseCurrency(aging?.[bucket.key]) / openBalanceTotal) * 100 : 0}%`,
                      backgroundColor: `var(--status-${index === 0 ? 'success' : index < 3 ? 'warning' : 'destructive'}-foreground)`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="space-y-1.5">
            <CardTitle>Customers With Open Balances</CardTitle>
            <CardDescription>Customer directory view limited to unpaid balances</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {hasCustomers ? (
            <IndexDataTable
              mode="client"
              data={filteredCustomers}
              columns={columns}
              rowLink={(customer) => path.customers.show(customer.id).using({ orgId }).link}
              getRowId={(customer) => String(customer.id)}
              toolbar={
                <IndexTableToolbar
                  primary={
                    <IndexTableSearchBox
                      value={searchTerm}
                      onCommit={onSearchChange}
                      countText={customerCountText(customerCount)}
                      isFetching={isFetching}
                      placeholder="Search customers..."
                    />
                  }
                />
              }
              emptyState={
                <Typography variant="supporting">
                  {searchTerm.trim()
                    ? 'No customers match your search.'
                    : 'No customers have an open balance for this date.'}
                </Typography>
              }
              tableClassName="text-sm"
            />
          ) : (
            <div className="py-10 text-center">
              <Typography variant="h4" tone="muted">
                No customers have an open balance for this date
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function customerCountText(total: number): string {
  return `${total} customer${total === 1 ? '' : 's'}`
}

function filterCustomerBalanceCustomers(
  customers: CustomerBalanceReportCustomer[],
  searchTerm: string,
  formatPhone: FormatPhone
): CustomerBalanceReportCustomer[] {
  const query = normalizeCustomerBalanceSearchValue(searchTerm)
  const digits = normalizeCustomerBalancePhoneSearchValue(searchTerm)
  const exactId = parseIndexExactIdSearch(searchTerm)

  if (exactId !== null) {
    return customers.filter((customer) => Number(customer.id) === exactId)
  }

  if (!query && !digits) {
    return customers
  }

  return customers.filter((customer) =>
    getCustomerBalanceSearchValues(customer, formatPhone).some((value) => {
      const normalizedValue = normalizeCustomerBalanceSearchValue(value)
      const normalizedDigits = normalizeCustomerBalancePhoneSearchValue(value)

      return (
        (query && normalizedValue.includes(query)) || (digits && normalizedDigits.includes(digits))
      )
    })
  )
}

function getCustomerBalanceSearchValues(
  customer: CustomerBalanceReportCustomer,
  formatPhone: FormatPhone
): (string | number | null)[] {
  const phone = customer.phone ?? ''

  return [customer.id, customer.name, customer.email, phone, phone ? formatPhone(phone) : '']
}

function normalizeCustomerBalanceSearchValue(value: string | number | null): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

function normalizeCustomerBalancePhoneSearchValue(value: string | number | null): string {
  return String(value ?? '').replace(/\D/g, '')
}
